/**
 * @fileoverview Shared inspection template editor shell component.
 * @module template-builder
 *
 * Responsibilities:
 * - Render the full inspection template editor chrome: header bar, tab bar, content area.
 * - Always render the Canvas tab via the shared TemplateCanvas component — guaranteed parity.
 * - Delegate non-canvas tab content to the host via a render-prop slot.
 * - Accept host-specific header content via a slot prop.
 *
 * Dependencies:
 * - TemplateCanvas from ui-admin (canvas tab content)
 * - TemplateCanvasProps from ui-admin (canvas prop contract)
 *
 * Architecture:
 * - This component is intentionally stateless — all tab state and data are owned by the host.
 * - The canvas tab is the only tab rendered by this shell; all other tabs use `renderTab`.
 * - Both system-web and tenant-web MUST use this component so canvas UI is never duplicated.
 *
 * Notes:
 * - Add the shell to ui-admin/src/index.ts exports after creating.
 * - Pass `allowDeferViolations={true}` for system-web where no department code sets exist.
 */

import React from 'react';
import { TemplateCanvas } from './TemplateCanvas';
import type { TemplateCanvasProps } from './TemplateCanvas';
import { themeCSS, useTheme } from '../../contexts/ThemeContext';

// ---------------------------------------------------------------------------
// Tab contract
// ---------------------------------------------------------------------------

/**
 * @contract TemplateEditorTab
 *
 * Purpose:
 * - Describes a single tab entry in the inspection template editor tab bar.
 *
 * Fields:
 * - id: unique string identifier; 'canvas' is reserved for the canvas tab.
 * - label: display text for the tab button.
 * - count: optional badge count shown next to the label.
 * - extra: optional ReactNode rendered inline inside the tab button after the label.
 * - requirementUnmet: when true, the tab is flagged with a red dot and red label
 *   to indicate an unmet publish requirement on that tab.
 *
 * Rules:
 * - At least one tab with id='canvas' must be present.
 */
export interface TemplateEditorTab {
  id: string;
  label: string;
  count?: number;
  /** Optional inline content placed after the label (e.g. a help/tour icon). */
  extra?: React.ReactNode;
  /** When true, mark the tab as having an unmet publish requirement (red dot + red label). */
  requirementUnmet?: boolean;
}

// ---------------------------------------------------------------------------
// Shell props
// ---------------------------------------------------------------------------

/**
 * @contract InspectionTemplateEditorShellProps
 *
 * Purpose:
 * - Full prop surface for the unified inspection template editor shell.
 *
 * Fields:
 * - tabs: ordered list of tabs; 'canvas' tab is always rendered by this shell.
 * - activeTab: currently active tab id controlled by the host.
 * - onTabChange: called when the user clicks a tab.
 * - headerSlot: ReactNode rendered inside the top header bar.
 * - renderTab: render-prop called for every non-canvas tab; returns null/undefined for empty.
 * - notice: optional banner rendered between the tab bar and content area.
 * - className: optional override for the outermost wrapper class.
 * - allowDeferViolations: when true, the violations module can be added without selecting code sets.
 *   Set this for system-web where no department context exists yet.
 * - ...canvasProps: all TemplateCanvasProps passed directly to TemplateCanvas.
 *
 * Rules:
 * - Hosts MUST use this shell instead of embedding TemplateCanvas directly in a page.
 * - Canvas tab id must always be 'canvas'.
 *
 * Sync:
 * - No data access; purely presentational shell.
 */
export interface InspectionTemplateEditorShellProps extends TemplateCanvasProps {
  tabs: TemplateEditorTab[];
  activeTab: string;
  onTabChange: (id: string) => void;
  headerSlot: React.ReactNode;
  renderTab: (tabId: string) => React.ReactNode;
  notice?: React.ReactNode;
  className?: string;
  allowDeferViolations?: boolean;
}

// ---------------------------------------------------------------------------
// InspectionTemplateEditorShell
// ---------------------------------------------------------------------------

/**
 * @component InspectionTemplateEditorShell
 *
 * Purpose:
 * - Shared full-page shell for editing an inspection template.
 * - Guarantees that the Canvas tab always renders the shared TemplateCanvas component
 *   in both system-web and tenant-web — eliminating UI divergence by construction.
 *
 * Props:
 * - tabs: tab definitions including the required 'canvas' entry.
 * - activeTab / onTabChange: controlled tab state owned by the host.
 * - headerSlot: host-provided top bar content (back button, title, save/publish actions).
 * - renderTab: host-provided render-prop for every non-canvas tab.
 * - notice: optional informational banner (e.g. draft-promoted alert).
 * - allowDeferViolations: system-web flag to allow adding a Violations module without code sets.
 * - ...canvasProps: all TemplateCanvasProps forwarded verbatim to TemplateCanvas.
 *
 * Behavior:
 * - Canvas tab renders TemplateCanvas; all other tabs render renderTab(activeTab).
 * - Tab bar shows badge count for non-canvas tabs when count > 0.
 *
 * Data Source:
 * - remote (via passed TemplateCanvasProps — host is responsible for data wiring)
 *
 * Constraints:
 * - Never adds its own data-fetching logic; stateless shell only.
 * - Tab id 'canvas' is reserved and cannot be used for custom tabs.
 *
 * Performance:
 * - TemplateCanvas is only mounted when the canvas tab is active.
 */
export function InspectionTemplateEditorShell({
  tabs,
  activeTab,
  onTabChange,
  headerSlot,
  renderTab,
  notice,
  className,
  allowDeferViolations,
  // All remaining props are TemplateCanvasProps
  ...canvasProps
}: InspectionTemplateEditorShellProps) {
  const { theme } = useTheme();
  const themeCssString = React.useMemo(() => themeCSS(theme), [theme]);

  return (
    <div
      className={`theme-grid ${className ?? 'h-full flex flex-col overflow-hidden'}`}
      style={{ background: theme.pageBg, color: theme.textPrimary }}
    >
      <style>{themeCssString}</style>

      {/* ── Header bar ── */}
      <div className="shrink-0 border-b border-gray-200 bg-white" style={{ background: theme.headerBg, borderColor: theme.headerBorder }}>
        {headerSlot}
      </div>

      {/* ── Tab bar ── */}
      <div className="shrink-0 border-b border-gray-200 bg-white" style={{ background: theme.toolbarBg, borderColor: theme.toolbarBorder }}>
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 inline-flex items-center gap-1.5 ${
                activeTab === tab.id
                  ? 'border-(--brand-primary) text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50'
              }`}
              style={activeTab === tab.id
                ? { borderBottomColor: theme.accent, color: theme.textPrimary }
                : { color: theme.textSecondary }}
            >
              {tab.requirementUnmet && (
                <span
                  className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0"
                  aria-hidden="true"
                  title="This tab has an unmet publish requirement"
                />
              )}
              <span style={tab.requirementUnmet ? { color: '#dc2626' } : undefined}>{tab.label}</span>
              {tab.extra}
              {tab.id !== 'canvas' && tab.count !== undefined && tab.count > 0 && (
                <span
                  className="text-xs bg-gray-200/80 text-gray-600 px-1.5 py-0.5 rounded-full"
                  style={{ background: theme.expandedBg, color: theme.textSecondary }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Optional notice banner ── */}
      {notice}

      {/* ── Content area ── */}
      <div className={`flex-1 min-h-0 flex ${activeTab === 'canvas' ? '' : 'overflow-hidden'}`} style={{ background: theme.pageBg }}>
        {activeTab === 'canvas' ? (
          <TemplateCanvas
            {...canvasProps}
            allowDeferViolations={allowDeferViolations}
          />
        ) : (
          renderTab(activeTab)
        )}
      </div>

    </div>
  );
}

export default InspectionTemplateEditorShell;
